// The Worklist's and History's column order as someone set it in the Columns menu: the columns that
// are not frozen, kept per person (as the menu keeps show / hide), reset to the default from it.
// Frozen columns stay where the table puts them; Routing window may be unpinned ("Pinned", `PINNABLE`).
// No imports: `scripts/routing-checks.ts` checks it.

/** The columns in someone's order: frozen ones first as they are, then the rest as moved. */
export function orderColumns<T extends { id: string; sticky?: boolean }>(
  columns: readonly T[],
  order: readonly string[],
): T[] {
  const frozen = columns.filter((c) => c.sticky);
  const rest = columns.filter((c) => !c.sticky);
  const at = new Map(order.map((id, i) => [id, i]));
  const out = rest.filter((c) => at.has(c.id)).sort((a, b) => at.get(a.id)! - at.get(b.id)!);
  // A column the saved order does not know (new since) goes where the default puts it: after the
  // column before it in the default order (a new one before it too), else first.
  rest.forEach((c, i) => {
    if (at.has(c.id)) return;
    const before = rest
      .slice(0, i)
      .reverse()
      .find((b) => out.includes(b));
    out.splice(before ? out.indexOf(before) + 1 : 0, 0, c);
  });
  return [...frozen, ...out];
}

/** The order after `moved` is dropped on `target`: before it, or `after` it. */
export function moveColumn(ids: readonly string[], moved: string, target: string, after: boolean): string[] {
  if (moved === target || !ids.includes(moved) || !ids.includes(target)) return [...ids];
  const without = ids.filter((id) => id !== moved);
  const i = without.indexOf(target) + (after ? 1 : 0);
  return [...without.slice(0, i), moved, ...without.slice(i)];
}

/** A change of the hidden columns' defaults: from `version` on, `hide` are hidden and `show` shown by default. */
export interface HiddenStep {
  version: number;
  hide: readonly string[];
  show?: readonly string[];
}

/**
 * Someone's hidden columns, saved at `version`, brought up to the defaults that came since — once:
 * each later step's new default-hidden columns hidden, its newly shown ones shown. A column they
 * showed or hid after that version is theirs.
 */
export function upgradeHidden(saved: readonly string[], version: number, steps: readonly HiddenStep[]): string[] {
  let out = [...saved];
  for (const step of steps) {
    if (step.version <= version) continue;
    out = [...new Set([...out.filter((id) => !(step.show ?? []).includes(id)), ...step.hide])];
  }
  return out;
}

/** The pinned columns a person may unpin ("Pinned" in the Columns menu): Routing window only, pinned by default. */
export const PINNABLE: readonly string[] = ["window"];

/** Someone's unpinned columns as stored with their column set, read defensively: pinnable ids only, each once. */
export const unpinnedOf = (raw: unknown): string[] =>
  Array.isArray(raw) ? [...new Set(raw.filter((x): x is string => typeof x === "string" && PINNABLE.includes(x)))] : [];

/** The unpinned after a "Pinned" tick on `id`: on pins it again, off unpins it. */
export const pinTick = (unpinned: readonly string[], id: string, pinned: boolean): string[] =>
  unpinnedOf(pinned ? unpinned.filter((x) => x !== id) : [...unpinned, id]);

/** The columns with those unpinned: no longer frozen, so they scroll with the rest, and may be moved and hidden. */
export const pinColumns = <T extends { id: string; sticky?: boolean }>(
  columns: readonly T[],
  unpinned: readonly string[],
): T[] => columns.map((c) => (c.sticky && unpinned.includes(c.id) ? { ...c, sticky: false } : c));
